/**
 * The profession nudge (CLA-1141 follow-up).
 *
 * The registration/profession split left a cohort with a registration answer
 * and no profession: 46 users on AU prod at the time of the split. Their note
 * prompt carries no `Role:` line at all — honest, but the profession is the
 * lever the specialty work needs, and nothing in the product asks for it.
 * `needsPractitionerIdentity` in `@/lib/identity-gate` deliberately does not:
 * its question is "has this person ever been asked?", not "is their profile
 * complete" — profile completeness is the nag's job. This is that nag.
 *
 * ## Why the gate reads registration_status too
 *
 * `!practitioner_type` alone is the wrong gate in two directions:
 *
 *   * A user with NEITHER field has never been asked anything and belongs to
 *     the onboarding wizard, not to a banner. Nudging them double-asks.
 *   * A user who answered `admin_only` — "I run the business, I don't practice
 *     clinically" — has no profession to give. A question with no truthful
 *     answer gets an untruthful one, so they are excluded outright: banner,
 *     Settings flag and sidebar dot all stay off for them.
 *
 * Deliberately NOT gated on `clinic_role === 'practitioner'` the way the
 * identity gate is. Clinic roles in prod are `practitioner`, `clinic_admin`
 * and **null** — an org-level practitioner (a doctor who belongs to the org
 * rather than to one clinic) carries no clinic role at all, and gating on it
 * would silently exclude them from a nudge aimed squarely at them. The
 * registration answer already says they practice.
 *
 * ## Snooze, not suppress
 *
 * The clinic-setup banner is non-dismissable because an unconfigured clinic
 * breaks things downstream. This doesn't break anything — so it takes "Not
 * now" and goes away for a week. A nag that cannot be silenced gets answered
 * dishonestly, which is worse than being answered late.
 *
 * The snooze is per user and lives in localStorage, so it is per browser. It
 * is on `clearSensitiveStorage`'s keep-list for the same reason
 * `claro:passkey-offer` is: wiping it on logout would re-ask on the very next
 * sign-in and turn a snooze into an un-dismissable nag for anyone who signs
 * out at the end of a day. Contents are a user id and an expiry timestamp —
 * no PHI.
 *
 * The snooze covers the BANNER only. The Settings flag and the sidebar dot
 * are the permanent state of an unset field, not a campaign, and stay put.
 */

import type { User } from '@/api/types'

export const SNOOZE_KEY_PREFIX = 'claro:role-nudge'
export const SNOOZE_DAYS = 7

const SNOOZE_MS = SNOOZE_DAYS * 24 * 60 * 60 * 1000

/** Registration answers that mean "I don't practice" — never nudged. */
const NON_PRACTISING = new Set(['admin_only'])

/**
 * Does this user have no profession recorded?
 *
 * Drives all three surfaces (banner, Settings flag, sidebar dot). Says
 * nothing about the snooze — the banner checks that separately.
 *
 * ⚠ CLA-1158 — this deliberately does NOT require a registration answer.
 *
 * It used to. That excluded exactly the people who most needed asking:
 * anyone added to a clinic as "Staff", "Clinic Admin" or "Viewer" never
 * passes `needsPractitionerIdentity` (it requires `clinic_role ===
 * 'practitioner'`), so they are never routed to the identity step, so
 * `registration_status` stays null — and with the old gate, the nudge then
 * stayed silent forever. `practitioner_type` remained null permanently.
 *
 * That is not cosmetic. `PractitionerType.ahpra_registered?/1` returns nil
 * for an unknown role, and the scope-of-practice guard in the note prompt
 * fires only on an explicit `false`. So the rail that says "do not record a
 * diagnosis, a prescription or an authorisation as theirs" never fired for
 * that population — and nothing gates note creation on role, so they could
 * write notes regardless.
 *
 * `admin_only` is still excluded, because that answer means "I don't
 * practise" and nagging them would be noise. The distinction that matters is
 * "said they don't practise" (silent) vs "never asked" (ask).
 */
export function needsProfession(
  user: Pick<User, 'practitioner_type' | 'registration_status'> | null | undefined,
): boolean {
  if (!user) return false
  if (user.practitioner_type?.trim()) return false
  const registration = user.registration_status?.trim()
  if (!registration) return true
  return !NON_PRACTISING.has(registration)
}

export function snoozeKey(userId: number): string {
  return `${SNOOZE_KEY_PREFIX}:user:${userId}`
}

export function isNudgeSnoozed(userId: number, now: number = Date.now()): boolean {
  try {
    const raw = localStorage.getItem(snoozeKey(userId))
    if (!raw) return false
    const until = Number(raw)
    // A corrupt value means "no snooze" rather than "snoozed forever" — the
    // failure mode of a bad parse should be asking again, not silence.
    if (!Number.isFinite(until)) return false
    return until > now
  } catch {
    return false
  }
}

export function snoozeNudge(userId: number, now: number = Date.now()): void {
  try {
    localStorage.setItem(snoozeKey(userId), String(now + SNOOZE_MS))
  } catch {
    /* storage unavailable (private mode, quota) — the banner just returns next load */
  }
}

export function clearNudgeSnooze(userId: number): void {
  try {
    localStorage.removeItem(snoozeKey(userId))
  } catch {
    /* ignore */
  }
}
